import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import readerApi from './services/readerApi';
import Dashboard from './pages/Dashboard/Dashboard';
import Login from './pages/Login/Login';
import Users from './pages/Users/Users';
import Locations from './pages/Locations/Locations';
import Warehouses from './pages/Warehouses/Warehouses';
import Rooms from './pages/Rooms/Rooms';
import Products from './pages/Products/Products';
import ProductList from './pages/ProductList/ProductList';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'font-awesome/css/font-awesome.min.css';
import { ToastProvider } from './Contexts/ToastContext/ToastContext';
import { AuthProvider, useAuth } from './Contexts/AuthContext/AuthContext';
import Navbar from './Components/Other/Navbar';
import 'leaflet/dist/leaflet.css';
import Operators from './pages/Operators/Operators';
import Cashier from './pages/Cashier/Cashier';
import AuditLog from './pages/AuditLog/AuditLog';
import ExpiryLog from './pages/ExpiryLog/ExpiryLog';
import TagDetails from './pages/TagDetails/TagDetails';
import Transfers from './pages/Transfers/Transfers';

const OperatorHome = "/operators";
const CashierHome = "/cashier";

const ProtectedRoute = ({
  children,
  requireAdmin = false,
  requireManagerOrAdmin = false,
  requireOperatorOrAdmin = false,
  requireCashierOrAdmin = false,
  denyOperator = false
}) => {
  const { isAuthenticated, loading, isAdmin, isManagerOrAdmin, isOperator, isCashier } = useAuth();

  if (loading) {
    return (
      <div className="dfCc" style={{ height: "100vh", width: "100vw" }}>
        <i className="fa fa-spinner fa-spin" style={{ fontSize: "48px", color: "#3B82F6" }}></i>
        <p style={{ marginTop: "16px", color: "#64748b" }}>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (denyOperator && isOperator()) {
    return <Navigate to={OperatorHome} />;
  }

  if (isCashier() && !requireCashierOrAdmin) {
    return <Navigate to={CashierHome} />;
  }

  if (requireAdmin && !isAdmin()) {
    return <Navigate to="/dashboard" />;
  }

  if (requireManagerOrAdmin && !isManagerOrAdmin()) {
    return <Navigate to="/dashboard" />;
  }

  if (requireOperatorOrAdmin && !isAdmin() && !isOperator()) {
    return <Navigate to="/dashboard" />;
  }

  if (requireCashierOrAdmin && !isAdmin() && !isCashier()) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

const LoginRedirect = () => {
  const { isOperator, isCashier } = useAuth();
  if (isOperator()) return <Navigate to={OperatorHome} />;
  if (isCashier()) return <Navigate to={CashierHome} />;
  return <Navigate to="/dashboard" />;
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  // Scan liveness heartbeat: ping `/heartbeat-scan` every 5s ONLY while a
  // normal (user-initiated) continuous scan is actually running.  Theft and
  // gate monitors live in their own backend dict and are never touched by
  // this watchdog, so there's no need to keep the heartbeat alive for them.
  //
  // We cheaply poll `/continuous-scan-status` each tick — if its
  // `activeScans` map is empty we do nothing, which avoids a pointless
  // heartbeat every 5s when the user isn't scanning.  When scans ARE
  // running and the tab is closed, the pings stop and the backend's
  // watchdog auto-stops the scans after SCAN_HEARTBEAT_TIMEOUT (15s).
  useEffect(() => {
    if (!isAuthenticated) return;
    const tick = async () => {
      try {
        const status = await readerApi.getContinuousScanStatus();
        const active = status?.activeScans && Object.keys(status.activeScans).length > 0;
        if (active) {
          await readerApi.heartbeatScan();
        }
      } catch { /* ignore — brief reader-backend outages shouldn't spam */ }
    };
    tick(); // immediate first check so the watchdog clock resets right away
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [isAuthenticated]);

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated ? <LoginRedirect /> : <Login />
      } />
      <Route path="/" element={
        <ProtectedRoute denyOperator>
          <>
            <Navbar />
            <Dashboard />
          </>
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute denyOperator>
          <>
            <Navbar />
            <Dashboard />
          </>
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute requireManagerOrAdmin={true}>
          <>
            <Navbar />
            <Users />
          </>
        </ProtectedRoute>
      } />
      <Route path="/products" element={
        <ProtectedRoute requireAdmin={true}>
          <>
            <Navbar />
            <Products />
          </>
        </ProtectedRoute>
      } />
      <Route path="/product-list" element={
        <ProtectedRoute requireAdmin={true}>
          <>
            <Navbar />
            <ProductList />
          </>
        </ProtectedRoute>
      } />
      <Route path="/locations" element={
        <ProtectedRoute denyOperator>
          <>
            <Navbar />
            <Locations />
          </>
        </ProtectedRoute>
      } />
      <Route path="/warehouses" element={
        <ProtectedRoute denyOperator>
          <>
            <Navbar />
            <Warehouses />
          </>
        </ProtectedRoute>
      } />
      <Route path="/rooms" element={
        <ProtectedRoute denyOperator>
          <>
            <Navbar />
            <Rooms />
          </>
        </ProtectedRoute>
      } />
      <Route path="/operators" element={
        <ProtectedRoute requireOperatorOrAdmin={true}>
          <>
            <Navbar />
            <Operators />
          </>
        </ProtectedRoute>
      } />
      <Route path="/cashier" element={
        <ProtectedRoute requireCashierOrAdmin={true}>
          <>
            <Navbar />
            <Cashier />
          </>
        </ProtectedRoute>
      } />
      <Route path="/audit-log" element={
        <ProtectedRoute requireAdmin={true}>
          <>
            <Navbar />
            <AuditLog />
          </>
        </ProtectedRoute>
      } />
      <Route path="/expiry-log" element={
        <ProtectedRoute requireAdmin={true}>
          <>
            <Navbar />
            <ExpiryLog />
          </>
        </ProtectedRoute>
      } />
      <Route path="/tags/:tid" element={
        <ProtectedRoute>
          <>
            <Navbar />
            <TagDetails />
          </>
        </ProtectedRoute>
      } />
      <Route path="/transfers" element={
        <ProtectedRoute requireManagerOrAdmin={true}>
          <>
            <Navbar />
            <Transfers />
          </>
        </ProtectedRoute>
      } />
      <Route path="*" element={<LoginRedirect />} />
    </Routes>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <ToastProvider>
        <BrowserRouter>
          <div style={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "var(--bg-page)",
            overflow: "hidden",
            position: "relative",
          }}>
            <AppRoutes />
          </div>
        </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;
